import type { AirQualityData, AirQualityParameter } from '@/types';
import { airQualityApi } from '@/lib/api';
import { AIR_QUALITY_API } from '@/constants/api';
import { useOptimizedQuery } from './useOptimizedQuery';

// Raw API response types
interface RawParameterReading {
  endtime: string;
  value: string;
  verification: number;
}

interface RawParameter {
  unit: string;
  resolution: string;
  [key: string]: string | RawParameterReading;
}

interface RawStationData {
  name: string;
  local_id: string;
  parameters: Record<string, RawParameter>;
}

type RawAirQualityResponse = Record<string, RawStationData>;

/**
 * Extract the latest reading (element "0") from a parameter
 */
function extractLatestReading(param: RawParameter): AirQualityParameter | undefined {
  const latestReading = param['0'] as RawParameterReading | undefined;
  if (!latestReading || typeof latestReading === 'string') {
    return undefined;
  }

  const value = parseFloat(latestReading.value);
  // Handle negative values (sensor calibration issues) - treat as 0
  const normalizedValue = value < 0 ? 0 : value;

  return {
    value: Math.round(normalizedValue * 10) / 10, // Round to 1 decimal place
    unit: param.unit,
    endtime: latestReading.endtime,
  };
}

/**
 * Transform raw API response to AirQualityData
 */
function transformAirQualityData(data: RawAirQualityResponse): AirQualityData | null {
  const stationData = data[AIR_QUALITY_API.stationId];
  if (!stationData) {
    return null;
  }

  const params = stationData.parameters;

  return {
    stationName: stationData.name,
    stationId: stationData.local_id,
    parameters: {
      pm25: params['PM2.5'] ? extractLatestReading(params['PM2.5']) : undefined,
      pm10: params['PM10'] ? extractLatestReading(params['PM10']) : undefined,
      h2s: params['H2S'] ? extractLatestReading(params['H2S']) : undefined,
      so2: params['SO2'] ? extractLatestReading(params['SO2']) : undefined,
      no2: params['NO2'] ? extractLatestReading(params['NO2']) : undefined,
    },
    lastUpdated: new Date().toISOString(),
  };
}

/**
 * Hook to fetch current air quality data for Reykjavik
 */
export function useAirQuality() {
  return useOptimizedQuery({
    queryKey: ['airQuality', AIR_QUALITY_API.stationId],
    queryFn: async () => {
      const data = await airQualityApi.getLatest();
      return transformAirQualityData(data);
    },
    tier: 'dynamic', // 1-hour cache, same as weather
  });
}

/**
 * Get air quality level based on PM2.5 value
 * Based on WHO Air Quality Guidelines
 */
export function getAirQualityLevel(pm25: number): 'good' | 'moderate' | 'unhealthy' | 'hazardous' {
  if (pm25 <= 10) return 'good';
  if (pm25 <= 25) return 'moderate';
  if (pm25 <= 50) return 'unhealthy';
  return 'hazardous';
}

/**
 * Get color for air quality level
 */
export function getAirQualityColor(level: 'good' | 'moderate' | 'unhealthy' | 'hazardous'): string {
  switch (level) {
    case 'good':
      return '#4CAF50'; // Green
    case 'moderate':
      return '#FFC107'; // Yellow
    case 'unhealthy':
      return '#FF9800'; // Orange
    case 'hazardous':
      return '#F44336'; // Red
  }
}
